import { LocalDate } from "@js-joda/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BackendAxiosError } from "services/api/backendError";
import { PAYMENT_METHOD_OPTIONS } from "../shared/staff.paymentMethod.logic";
import type {
  EditingTab,
  EmploymentDetailsForm,
  PaymentMethodDetailsForm,
  PersonalDetailsForm,
  ProfilePanelTab,
  SelectOption,
} from "./profilePanel.contract";
import { fetchEmployeeNumberForProfilePanel } from "./domains/employment/profilePanel.employeeNumber.logic";
import { resolveStaffProfilePanelErrorMessage } from "./domains/employment/profilePanel.error.logic";
import { toEmploymentOptions } from "./domains/employment/profilePanel.options.logic";
import { toLocalDateOrNull } from "./domains/employment/profilePanel.payload.logic";
import {
  toEmploymentUiRules,
  withEmploymentTypeChangePrefill,
  withNormalizedEmploymentFormByRules,
} from "../shared/staffEmploymentRules.logic";
import type {
  UseStaffProfilePanelStateParams,
  UseStaffProfilePanelStateResult,
} from "./state/profilePanel.state.contract";
import {
  EMPTY_EMPLOYMENT_DETAILS,
  EMPTY_PAYMENT_METHOD_DETAILS,
  EMPTY_PERSONAL_DETAILS,
  toEmploymentDetailsForm,
  toPersonalDetailsForm,
} from "./state/profilePanel.state.formMappers.logic";
import {
  canSavePaymentMethod,
  resolvePaymentMethodError,
} from "./state/profilePanel.state.paymentMethod.logic";
import { savePersonalProfile } from "./state/profilePanel.state.personal.logic";
import { performSaveEmployment as runPerformSaveEmployment } from "./state/profilePanel.state.employment.logic";
import { useExecuteSavingAction } from "./state/profilePanel.state.saveAction.logic";
import { useProfilePanelModalsState } from "./state/profilePanel.state.modals.logic";
import { toTeamOptions } from "./state/profilePanel.state.template.logic";
import {
  canSaveEmploymentEdit as toCanSaveEmploymentEdit,
  canSavePersonalEdit as toCanSavePersonalEdit,
} from "./state/profilePanel.state.validation.logic";

export const useStaffProfilePanelState = (
  params: UseStaffProfilePanelStateParams,
): UseStaffProfilePanelStateResult => {
  const companyAddress = params.companyAddress;
  const companyId = params.companyId;
  const companySettings = params.companySettings;
  const initialTab = params.initialTab;
  const isOpen = params.isOpen;
  const onWorkerUpdated = params.onWorkerUpdated;
  const profile = params.profile;
  const applySavedProfile = params.applySavedProfile;
  const reloadProfile = params.reloadProfile;
  const subscription = params.subscription;
  const t = params.t;
  const unitTeamOptions = params.unitTeamOptions;
  const workerId = params.workerId;
  const [activeTab, setActiveTab] = useState<ProfilePanelTab>(
    initialTab ?? "profile",
  );
  const [editingTab, setEditingTab] = useState<EditingTab>(null);

  const [isAddEmploymentPanelOpen, setIsAddEmploymentPanelOpen] =
    useState(false);
  const [editedPersonalDetails, setEditedPersonalDetails] =
    useState<PersonalDetailsForm>(EMPTY_PERSONAL_DETAILS);
  const [editedEmploymentDetails, setEditedEmploymentDetails] =
    useState<EmploymentDetailsForm>(EMPTY_EMPLOYMENT_DETAILS);
  const [editedPaymentMethodDetails, setEditedPaymentMethodDetails] =
    useState<PaymentMethodDetailsForm>(EMPTY_PAYMENT_METHOD_DETAILS);
  const [hasTouchedPaymentMethodValue, setHasTouchedPaymentMethodValue] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showLockedEditHint, setShowLockedEditHint] = useState(false);
  const employeeNumberRequestIdRef = useRef(0);
  const employmentRules = useMemo(() => {
    return toEmploymentUiRules({
      companySettings,
      employmentType: editedEmploymentDetails.employmentType,
      salaryType: editedEmploymentDetails.salaryType,
      subscription,
    });
  }, [
    companySettings,
    editedEmploymentDetails.employmentType,
    editedEmploymentDetails.salaryType,
    subscription,
  ]);

  const employmentOptions = useMemo(() => {
    return toEmploymentOptions(params.t, employmentRules);
  }, [employmentRules, params.t]);

  const paymentMethodOptions: SelectOption[] = PAYMENT_METHOD_OPTIONS.map(
    (paymentMethodOption) => ({
      value: paymentMethodOption.value,
      label: t(paymentMethodOption.label),
    }),
  );

  const teamOptions = useMemo(() => {
    return toTeamOptions(unitTeamOptions);
  }, [unitTeamOptions]);

  const selectedEmploymentStartDate = useMemo(() => {
    return toLocalDateOrNull(profile?.employmentInfo?.selected?.startDate);
  }, [profile?.employmentInfo]);
  const selectedEmploymentId = profile?.employmentInfo?.selected?.id ?? null;

  const executeSavingAction = useExecuteSavingAction({
    resolveErrorMessage: (error: BackendAxiosError): string =>
      resolveStaffProfilePanelErrorMessage(error, t, {
        paymentMethodType: editedPaymentMethodDetails.type,
      }),
    setIsSaving,
    setSaveError,
  });

  // A new employment is added in the add staff panel, which owns its own form.
  const startCreatingEmployment = useCallback((): void => {
    setIsAddEmploymentPanelOpen(true);
    setSaveError("");
  }, []);

  const closeAddEmploymentPanel = useCallback((): void => {
    setIsAddEmploymentPanelOpen(false);
  }, []);

  const {
    canFetchTaxTable,
    isFetchingTaxTable,
    isTaxTableFetched,
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    isDiscardConfirmOpen,
    isSalaryModalOpen,
    isTaxModalOpen,
    isPaymentMethodModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    isChangeToPermanentModalOpen,
    setEffectiveFromDate,
    setEndEmploymentDate,
    openEndEmploymentForCreateModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleFetchTaxTable,
    handleUpdateTaxDraftValue,
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    handleOpenChangeToPermanentModal,
    handleCloseChangeToPermanentModal,
    handleConfirmChangeToPermanent,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
  } = useProfilePanelModalsState({
    companyId,
    selectedEmploymentId,
    selectedEmploymentStartDate,
    editedEmploymentDetails,
    editedPaymentMethodDetails,
    employmentRules,
    executeSavingAction,
    isOpen,
    isSaving,
    onWorkerUpdated,
    profile,
    applySavedProfile,
    reloadProfile,
    setEditedEmploymentDetails,
    setEditedPaymentMethodDetails,
    setHasTouchedPaymentMethodValue,
    setIsSaving,
    setSaveError,
    translate: t,
    workerId,
    onStartCreatingEmployment: startCreatingEmployment,
  });

  useEffect(() => {
    const resetTab = initialTab ?? "profile";
    if (!isOpen) {
      setActiveTab(resetTab);
      setEditingTab(null);
      setIsAddEmploymentPanelOpen(false);
      setEditedPersonalDetails(EMPTY_PERSONAL_DETAILS);
      setEditedEmploymentDetails(EMPTY_EMPLOYMENT_DETAILS);
      setEditedPaymentMethodDetails(EMPTY_PAYMENT_METHOD_DETAILS);
      setHasTouchedPaymentMethodValue(false);
      setIsLoadingEmployeeNumber(false);
      setSaveError("");
      setShowLockedEditHint(false);
      return;
    }

    setActiveTab(resetTab);
    setEditingTab(null);
    setIsAddEmploymentPanelOpen(false);
    setSaveError("");
    setShowLockedEditHint(false);
    setHasTouchedPaymentMethodValue(false);
  }, [initialTab, isOpen, workerId]);

  useEffect(() => {
    if (!isOpen) return;
    if (editingTab === "profile") return;

    setEditedPersonalDetails((previousValue) => ({
      ...previousValue,
      teamIds:
        profile?.workGroups.map((workGroup) => String(workGroup.id)) ?? [],
    }));
  }, [editingTab, isOpen, profile?.workGroups]);

  useEffect(() => {
    if (editingTab !== "employmentSalary") return;

    const normalizedValues = withNormalizedEmploymentFormByRules(
      {
        salaryType: editedEmploymentDetails.salaryType,
        employeeCategory: editedEmploymentDetails.employeeCategory,
        paymentType: editedEmploymentDetails.paymentType,
      },
      employmentRules,
    );

    if (
      normalizedValues.salaryType === editedEmploymentDetails.salaryType &&
      normalizedValues.employeeCategory ===
        editedEmploymentDetails.employeeCategory &&
      normalizedValues.paymentType === editedEmploymentDetails.paymentType
    ) {
      return;
    }

    setEditedEmploymentDetails((previousValue) => ({
      ...previousValue,
      salaryType: normalizedValues.salaryType,
      employeeCategory: normalizedValues.employeeCategory,
      paymentType: normalizedValues.paymentType,
    }));
  }, [editedEmploymentDetails, editingTab, employmentRules]);

  const updatePersonalField = useCallback(
    <TField extends keyof PersonalDetailsForm>(
      field: TField,
      value: PersonalDetailsForm[TField],
    ): void => {
      setEditedPersonalDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
      setSaveError("");
    },
    [],
  );

  const updateEmploymentField = useCallback(
    <TField extends keyof EmploymentDetailsForm>(
      field: TField,
      value: EmploymentDetailsForm[TField],
    ): void => {
      setEditedEmploymentDetails((previousValue) =>
        withEmploymentTypeChangePrefill({
          changedField: field,
          changedValue: value,
          companyAddress,
          form: { ...previousValue, [field]: value },
        }),
      );
    },
    [companyAddress],
  );

  const updatePaymentMethodField = useCallback(
    <TField extends keyof PaymentMethodDetailsForm>(
      field: TField,
      value: PaymentMethodDetailsForm[TField],
    ): void => {
      setEditedPaymentMethodDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
      if (field === "value") {
        setHasTouchedPaymentMethodValue(true);
      }
      setSaveError("");
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    fetchEmployeeNumberForProfilePanel({
      companyId,
      requestIdRef: employeeNumberRequestIdRef,
      setIsLoadingEmployeeNumber,
      setEditedEmploymentDetails,
    });
  }, [companyId]);

  const handleTabChange = useCallback((value: string): void => {
    if (
      value === "profile" ||
      value === "employmentSalary" ||
      value === "history" ||
      value === "agreements"
    ) {
      setSaveError("");
      setActiveTab(value);
    }
  }, []);

  const handleEditClick = useCallback((): void => {
    if (activeTab === "profile") {
      setEditedPersonalDetails(toPersonalDetailsForm(profile));
      setEditingTab("profile");
      return;
    }

    if (activeTab === "history") {
      return;
    }

    setEditedEmploymentDetails(toEmploymentDetailsForm(profile));
    setEditingTab("employmentSalary");
  }, [activeTab, profile]);

  const handleAddEmploymentClick = useCallback((): void => {
    // An active, open-ended employment has to get an end date before another
    // employment can follow it. When the worker has an active employment, it
    // is the selected one.
    const hasActiveEmployment =
      profile?.employments.some((employment) => employment.isActive) === true;
    const selectedEmployment = profile?.employmentInfo?.selected;
    if (hasActiveEmployment && selectedEmployment?.endDate == null) {
      openEndEmploymentForCreateModal();
      return;
    }

    startCreatingEmployment();
  }, [openEndEmploymentForCreateModal, profile, startCreatingEmployment]);

  const handleCancelEdit = useCallback((): void => {
    employeeNumberRequestIdRef.current += 1;
    setIsLoadingEmployeeNumber(false);
    setEditingTab(null);
    setSaveError("");
  }, []);

  const performSaveEmployment = useCallback(
    (startDate: LocalDate | null, endDate: LocalDate | null): void => {
      runPerformSaveEmployment({
        companyId,
        workerId,
        profile,
        editedEmploymentDetails,
        editingTab,
        employmentRules,
        startDate,
        endDate,
        translate: t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => setEditingTab(null),
        setModalState: (): void => undefined,
        applySavedProfile,
        reloadProfile,
        onWorkerUpdated,
      });
    },
    [
      applySavedProfile,
      companyId,
      editedEmploymentDetails,
      editingTab,
      employmentRules,
      onWorkerUpdated,
      profile,
      reloadProfile,
      t,
      workerId,
    ],
  );

  const handleOpenSaveModal = useCallback((): void => {
    if (editingTab === "profile") {
      if (profile?.isRegistered !== false) return;
      if (companyId == null || workerId == null) return;
      if (!toCanSavePersonalEdit(editedPersonalDetails)) {
        setSaveError(t("Staff.ProfilePanelValidationError"));
        return;
      }

      savePersonalProfile({
        companyId,
        workerId,
        editedPersonalDetails,
        onWorkerUpdated,
        reloadProfile,
        setEditingTab,
        closeModal: (): void => undefined,
        setIsSaving,
        setSaveError,
        translate: t,
      });
      return;
    }

    if (editingTab !== "employmentSalary") return;

    const selectedEmployment = profile?.employmentInfo?.selected;
    const nextStartDate =
      editedEmploymentDetails.startDate ??
      toLocalDateOrNull(selectedEmployment?.startDate);
    const nextEndDate = editedEmploymentDetails.endDate;

    performSaveEmployment(nextStartDate, nextEndDate);
  }, [
    companyId,
    editedEmploymentDetails.endDate,
    editedEmploymentDetails.startDate,
    editedPersonalDetails,
    editingTab,
    onWorkerUpdated,
    performSaveEmployment,
    profile,
    reloadProfile,
    t,
    workerId,
  ]);
  const canSavePersonalEdit = toCanSavePersonalEdit(editedPersonalDetails);
  const canSaveEmploymentEdit = useMemo((): boolean => {
    return toCanSaveEmploymentEdit({
      editingTab,
      editedEmploymentDetails,
      isEndDateRequiredOnSave: employmentRules.isEndDateRequiredOnSave,
    });
  }, [
    editedEmploymentDetails,
    editingTab,
    employmentRules.isEndDateRequiredOnSave,
  ]);

  const paymentMethodError = useMemo((): string => {
    return resolvePaymentMethodError(
      editedPaymentMethodDetails,
      hasTouchedPaymentMethodValue,
      t,
    );
  }, [editedPaymentMethodDetails, hasTouchedPaymentMethodValue, t]);

  const canSavePaymentMethodValue = useMemo((): boolean => {
    return canSavePaymentMethod(editedPaymentMethodDetails);
  }, [editedPaymentMethodDetails]);

  return {
    activeTab,
    editingTab,
    isAddEmploymentPanelOpen,
    editedPersonalDetails,
    editedEmploymentDetails,
    editedPaymentMethodDetails,
    paymentMethodError,
    canFetchTaxTable,
    canSavePaymentMethod: canSavePaymentMethodValue,
    isSaving,
    isFetchingTaxTable,
    isTaxTableFetched,
    isLoadingEmployeeNumber,
    isSalaryModalOpen,
    isTaxModalOpen,
    isPaymentMethodModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    isChangeToPermanentModalOpen,
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    salaryEarliestStartDate: selectedEmploymentStartDate,
    saveError,
    showLockedEditHint,
    employmentRules,
    employmentOptions,
    paymentMethodOptions,
    teamOptions,
    canSavePersonalEdit,
    canSaveEmploymentEdit,
    isDiscardConfirmOpen,
    setShowLockedEditHint,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
    handleTabChange,
    handleEditClick,
    handleAddEmploymentClick,
    handleCancelEdit,
    closeAddEmploymentPanel,
    handleOpenSaveModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleFetchTaxTable,
    handleUpdateTaxDraftValue,
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    handleOpenChangeToPermanentModal,
    handleCloseChangeToPermanentModal,
    handleConfirmChangeToPermanent,
    fetchEmployeeNumber,
    updatePersonalField,
    updateEmploymentField,
    updatePaymentMethodField,
    setEffectiveFromDate,
    setEndEmploymentDate,
  };
};
